import { APPROVALS, CODE_LABEL, DELEGS, DEL_TASKS, DONE, TASKS, type ApRow, type DoneRow, type TaskRow } from './tasks';

// Mint-Tasks · trang chi tiết 2:1 (code board khối D21-NB: `SP` / `dt`). Chỉ đọc; phiếu KHÔNG mang số tiền (money=hide).

export type DetailKind = 'task' | 'ap' | 'del' | 'done';
export type Tone = 'err' | 'warn' | '';
export type StepSt = 'done' | 'cur' | 'rej' | 'todo';
export interface DChip { k: string; tone: 'err' | 'warn' | 'ok' | 'pri' | 'mute' | 'code' }
export interface DTodo { id: string; k: string; who: string; due: string; tone: Tone; done?: boolean; act?: string }
export interface DStep { seat: string; who: string; st: StepSt; stK: string; sub: string }
export interface DProp { k: string; v: string; tone?: 'err' | 'link'; href?: boolean }
export interface DAct { k: string; who: string; when: string; kind: string }
export interface DLink { k: string; sub: string; to: 'list' | 'appr' | 'deleg' | 'done' | 'open'; label?: string }
export interface DTimeline { k: string; v: string; st: 'd' | 'c' | 'n' }
export interface DetailView {
  kicker: string; title: string; chips: DChip[]; rule: string;
  gate?: { tone: 'err' | 'warn' | 'pri'; k: string; v: string };
  flags: Array<{ k: string; tip: string; tone: 'warn' | 'pri' }>; note: string; isAp: boolean;
  timeline: DTimeline[]; timelineK: string;
  todos: DTodo[]; info: DProp[]; steps: DStep[]; stepsK: string;
  people: Array<{ u: string; role: string; hei: boolean }>;
  links: DLink[]; acts: DAct[];
  primary: { label: string; kind: 'open' | 'done' | 'approve' | 'deleg' | 'none'; target?: string };
  menu: string[];
}

export const whoOf = (s: string) => s.replace(/^\[[^\]]*\]\s*/, '').split(/[ ·—]/)[0] || 'Hệ thống';
export const seatOf = (s: string) => s.match(/^\[[^\]]*\]/)?.[0] ?? '';

/** nơi mở đối tượng của từng loại việc (board `LINK`) */
export const LINK: Record<string, string> = {
  FRT: 'Mở job để khoá cước', VCN: 'Mở job · soạn báo khách', BKO: 'Mở RQ ở Booking', LOI: 'Mở B/L · LOI', DMD: 'Mở hàng nhập & cont', '@': 'Mở chat job',
  TFN: 'Mở chuyến để Finish', PRC: 'Mở báo giá', GPW: 'Mở duyệt giá ở Chi phí', DUN: 'Mở công nợ', DCF: 'Mở debit chờ xác nhận', SLD: 'Mở phiếu',
  OPF: 'Mở cơ hội ở Pipeline', CCF: 'Mở dịch vụ thông quan',
};
const GATE_LABEL = ['Nhắc', 'Cảnh báo', 'Chặn'] as const;
const GATE_TONE = ['pri', 'warn', 'err'] as const;

function codeTone(c: string): DChip['tone'] {
  if (c === 'GPW' || c === 'DUN') return 'err';
  if (c === 'MEMO' || c === '@') return 'mute';
  return 'code';
}

export function findDetail(kind: string, id: string): { kind: DetailKind; id: number | string } | null {
  if (kind === 'task' && TASKS.some((x) => String(x.id) === id)) return { kind, id: Number(id) };
  if (kind === 'ap' && APPROVALS.some((x) => String(x.id) === id)) return { kind, id: Number(id) };
  if (kind === 'del' && DELEGS.some((x) => x.id === id)) return { kind, id };
  if (kind === 'done' && DONE.some((x) => String(x.id) === id)) return { kind, id: Number(id) };
  return null;
}

const base = (): DetailView => ({
  kicker: '', title: '', chips: [], rule: '', flags: [], note: '', isAp: false, timeline: [], timelineK: '', todos: [], info: [], steps: [], stepsK: '',
  people: [], links: [], acts: [], primary: { label: '', kind: 'none' }, menu: [],
});

function taskView(x: TaskRow, doneNow: boolean): DetailView {
  const v = base();
  const who = whoOf(x.seat), seat = seatOf(x.seat);
  const over = x.grp === 'over', mgr = x.seat.includes('SALE-MGR');
  const open = LINK[x.code];
  v.kicker = `Việc · ${x.code} · ${CODE_LABEL[x.code] ?? ''}`; v.title = x.title;
  v.chips = [{ k: over ? 'Quá hạn' : x.grp === 'today' ? 'Hôm nay' : 'Sắp tới', tone: over ? 'err' : 'mute' }, { k: x.code, tone: codeTone(x.code) }];
  if (x.ext) v.chips.push({ k: `Đã gia hạn ${x.ext}/3`, tone: 'mute' });
  v.rule = 'Một task = một (loại, đối tượng). Sinh và đóng theo trạng thái dữ liệu; hạn = mốc ± giờ làm việc. KPI đúng / trễ hạn tính cho người xử lý thực tế.';
  if (x.gate > 0) v.gate = { tone: GATE_TONE[x.gate], k: `Cổng quá hạn · ${GATE_LABEL[x.gate]}`, v: x.gate === 2 ? `Đang chặn đúng thao tác: ${x.gateAct}. Không chặn xem dữ liệu, mail, chính việc này. Trưởng bộ phận mở tạm 2h.` : `Đi tiếp ${x.gateAct} phải ghi một dòng lý do.` };
  v.info = [
    { k: 'Đối tượng', v: x.obj || '—', tone: 'link' }, { k: 'Chi tiết', v: x.sub },
    { k: 'Hạn', v: `${x.due} (${x.dueK})`, tone: over ? 'err' : undefined }, { k: 'Giao cho', v: x.seat },
    { k: 'Sinh khi', v: x.code === 'FRT' ? 'Đã BKCF, chưa khoá phí' : x.code === 'GPW' ? 'Giá dự kiến dưới sàn tuyến' : `Theo task_rule ${x.code}` },
    { k: 'Tự đóng khi', v: x.code === 'FRT' ? 'Đã khoá phí' : x.code === 'GPW' ? 'Giá đạt sàn hoặc được duyệt' : 'Điều kiện sinh hết đúng' },
  ];
  v.timelineK = 'Lịch sử & leo thang';
  v.timeline = [
    { k: 'Sinh tự động', v: `21/Oct/2026 14:02 · task_rule ${x.code}`, st: 'd' },
    { k: 'Gia hạn +4h', v: x.ext ? `${x.ext} lần · ${x.noExt ? 'loại này cấm gia hạn' : 'tối đa 3'}` : 'chưa gia hạn', st: x.ext ? 'd' : 'n' },
    { k: 'Leo thang', v: mgr ? 'Trưởng KD trùng người → lên BOD [BOD-DIR] PHILIP' : 'Trưởng bộ phận của người nhận', st: over ? 'c' : 'n' },
  ];
  v.todos = [{ id: 'do', k: `Xử lý ${x.code} · ${x.obj || x.title}`, who, due: `${x.due} · ${x.dueK}`, tone: over ? 'err' : x.grp === 'today' ? 'warn' : '', done: doneNow, act: open ? undefined : 'Xong' }];
  if (x.gate > 0) v.todos.push({ id: 'gate', k: `Cổng quá hạn: ${x.gateAct}`, who, due: x.gate === 2 ? 'đang chặn' : 'cần lý do', tone: 'err' });
  if (!x.noExt) v.todos.push({ id: 'ext', k: 'Gia hạn +4 giờ làm việc', who, due: `đã dùng ${x.ext}/3`, tone: '', act: '+4h' });
  v.steps = [
    { seat: 'Hệ thống', who: 'Hệ thống', st: 'done', stK: 'Đã sinh', sub: `task_rule ${x.code} · 21/Oct/2026 14:02` },
    { seat: seat || 'Người nhận', who, st: doneNow ? 'done' : over ? 'rej' : 'cur', stK: doneNow ? 'Xong' : over ? 'Quá hạn' : 'Đang xử lý', sub: `Hạn ${x.due}` },
    { seat: mgr ? '[BOD-DIR]' : 'Trưởng bộ phận', who: mgr ? 'PHILIP' : 'Trưởng bộ phận', st: over && !doneNow ? 'cur' : 'todo', stK: 'Leo thang', sub: mgr ? 'Trưởng KD trùng người → lên BOD' : 'Khi quá hạn' },
  ];
  v.stepsK = 'Người nhận → leo thang';
  v.people = [{ u: who, role: `${seat} người được giao`, hei: who !== 'DAVID' }, ...(mgr ? [{ u: 'PHILIP', role: '[BOD-DIR] nhận leo thang', hei: true }] : [])];
  v.links = [...(open ? [{ k: open, sub: x.obj, to: 'open' as const, label: open }] : []), { k: 'Danh sách việc', sub: 'Về Việc & Duyệt', to: 'list' }];
  v.acts = [
    ...(doneNow ? [{ k: 'Đánh dấu xong', who, when: '22/Oct/2026 10:40', kind: 'Xong' }] : []),
    ...(x.ext ? [{ k: `Gia hạn +4h · ${x.ext} lần`, who, when: '22/Oct/2026 08:10', kind: 'Gia hạn' }] : []),
    { k: `Sinh tự động · task_rule ${x.code}`, who: 'Hệ thống', when: '21/Oct/2026 14:02', kind: 'Sinh' },
  ];
  v.primary = open ? { label: open, kind: 'open', target: open } : { label: 'Đánh dấu xong', kind: 'done' };
  v.menu = ['Chờ bên ngoài'];
  return v;
}

const AP_RULE = 'Người duyệt hiện theo ghế. Ghế trống → người được giao tạm vai trò → đường báo cáo → BOD. Một người không ký hai bước của cùng phiếu.';
function apView(a: ApRow, ok: boolean): DetailView {
  const v = base();
  const cur = a.steps[a.cur] ?? a.steps[0]!;
  v.isAp = true; v.note = a.note || '—'; v.flags = a.flags.map((f) => ({ ...f }));
  v.kicker = `Phiếu duyệt · ${a.kindLabel}`; v.title = a.title;
  v.chips = [{ k: `Bước ${a.cur + 1}/${a.steps.length}`, tone: 'pri' }, ...a.flags.map((f) => ({ k: f.k, tone: f.tone === 'warn' ? 'warn' as const : 'pri' as const }))];
  v.rule = AP_RULE;
  v.info = [{ k: 'Người đề nghị', v: a.by }, { k: 'Gửi lúc', v: `${a.when}/2026` }, { k: 'Ghi chú', v: a.note }, { k: 'Loại phiếu', v: a.kindLabel }, { k: 'Chứng từ', v: a.title, href: true }];
  v.todos = [
    { id: 'ap', k: `${ok ? 'Đã xử lý · ' : `Duyệt bước ${a.cur + 1} · `}${cur[0]}`, who: whoOf(cur[1]), due: `gửi ${a.when}`, tone: ok ? '' : 'warn', done: ok, act: ok ? undefined : 'Đồng ý' },
    ...a.flags.map((f, i) => ({ id: `f${i}`, k: f.k, who: 'Hệ thống', due: 'mở từng phiếu', tone: 'warn' as Tone })),
  ];
  v.steps = a.steps.map(([kk, s, d], i) => {
    const done = d === 'd' || (ok && i === a.cur);
    const empty = /trống/.test(s);
    return {
      seat: seatOf(s) || kk,
      who: empty ? `Ghế trống → ${s.match(/BOD-DIR (\w+)/)?.[1] ?? 'BOD'}` : /Acting: (\w+)/.test(s) ? s.match(/Acting: (\w+)/)![1]! : whoOf(s),
      st: done ? 'done' : i === a.cur ? 'cur' : 'todo', stK: done ? 'Xong' : i === a.cur ? 'Đang chờ' : 'Chưa tới', sub: kk + (/—/.test(s) ? ` · ${s.split('—')[1]!.trim()}` : ''),
    };
  });
  v.stepsK = `Bước ${a.cur + 1}/${a.steps.length}`;
  const ppl: Array<{ u: string; role: string; hei: boolean }> = [{ u: whoOf(a.by), role: `${seatOf(a.by)} người đề nghị`, hei: true }];
  a.steps.slice(1).forEach(([kk, s]) => ppl.push({ u: /trống/.test(s) ? 'PHILIP' : /Acting: (\w+)/.test(s) ? s.match(/Acting: (\w+)/)![1]! : whoOf(s), role: `${seatOf(s) || kk} · ${kk}`, hei: true }));
  v.people = ppl.filter((x, i, arr) => arr.findIndex((y) => y.u === x.u) === i);
  v.links = [{ k: 'Mở chứng từ', sub: a.title, to: 'open', label: 'Mở chứng từ' }, { k: 'Phiếu chờ duyệt', sub: 'Về tab Duyệt chờ tôi', to: 'appr' }];
  v.acts = [
    ...(ok ? [{ k: 'Đồng ý · chuyển bước kế theo ghế', who: 'DAVID', when: '22/Oct/2026 10:40', kind: 'Duyệt' }] : []),
    ...a.steps.filter((s) => s[2] === 'd').slice(1).map((s) => ({ k: `Duyệt · ${s[0]}`, who: whoOf(s[1]), when: `${a.when}/2026`, kind: 'Duyệt' })).reverse(),
    { k: `Gửi phiếu ${a.kindLabel}`, who: whoOf(a.by), when: `${a.when}/2026`, kind: 'Gửi' },
  ];
  v.primary = ok ? { label: 'Mở chứng từ', kind: 'open', target: 'Mở chứng từ' } : { label: 'Đồng ý', kind: 'approve' };
  v.menu = ok ? ['Mở chứng từ'] : ['Từ chối', 'Hỏi thêm người đề nghị', 'Mở chứng từ'];
  return v;
}

function delView(id: string): DetailView {
  const d = DELEGS.find((x) => x.id === id) ?? DELEGS[0]!;
  const v = base();
  v.kicker = 'Uỷ quyền đang hiệu lực'; v.title = `Thay cho ${d.k}`; v.chips = [{ k: 'Đang hiệu lực', tone: 'ok' }];
  v.rule = 'Uỷ quyền tra cứu: không sửa job/task; mọi nơi hỏi "người phụ trách hiệu lực" tại thời điểm đó.';
  v.info = [{ k: 'Nguồn', v: d.src }, { k: 'Phạm vi', v: d.scope }, { k: 'Thời gian', v: d.range }, { k: 'Quyền duyệt', v: d.appr }];
  v.timelineK = 'Hết kỳ';
  v.timeline = [{ k: 'Hết kỳ', v: `Việc tự về ${d.k}, không còn việc sót`, st: 'n' }, { k: 'Hoàn trả', v: `${d.k} đánh dấu nhận lại từng việc`, st: 'n' }];
  v.todos = [{ id: 'ret', k: 'Hoàn trả việc khi hết kỳ uỷ quyền', who: d.k, due: d.range, tone: '' }];
  v.steps = [{ seat: 'Người giao', who: d.k, st: 'done', stK: 'Đã giao', sub: d.src }, { seat: 'Người nhận', who: 'DAVID', st: 'cur', stK: 'Đang thay', sub: d.range }];
  v.stepsK = 'Uỷ quyền';
  v.people = [{ u: d.k, role: 'Người được thay', hei: true }, { u: 'DAVID', role: 'Đang nhận uỷ quyền', hei: false }];
  v.links = [{ k: 'Việc trong kỳ', sub: 'Tab Tôi đang thay', to: 'deleg' }];
  v.acts = [{ k: `Uỷ quyền có hiệu lực · ${d.range}`, who: 'Hệ thống', when: d.range.split('→')[0]!.trim(), kind: 'Uỷ quyền' }];
  v.primary = { label: 'Xem việc trong kỳ', kind: 'deleg' };
  void DEL_TASKS;
  return v;
}

function doneView(d: DoneRow): DetailView {
  const v = base();
  const open = LINK[d.code];
  v.kicker = `Đã xong · ${d.code}`; v.title = d.title; v.chips = [{ k: d.ont ? 'Đúng hạn' : 'Trễ hạn', tone: d.ont ? 'ok' : 'err' }];
  v.rule = 'Việc đã xong được lưu để đo KPI đúng / trễ hạn theo người và theo ghế.';
  v.info = [{ k: 'Đối tượng', v: d.obj, tone: 'link' }, { k: 'Chi tiết', v: d.sub }, { k: 'Xong lúc', v: d.at }];
  v.steps = [{ seat: 'Người xử lý', who: 'DAVID', st: 'done', stK: d.ont ? 'Đúng hạn' : 'Trễ hạn', sub: `Xong ${d.at}` }];
  v.stepsK = d.ont ? 'Đúng hạn' : 'Trễ hạn';
  v.people = [{ u: 'DAVID', role: 'Người xử lý thực tế', hei: false }];
  v.links = [...(open ? [{ k: 'Mở đối tượng', sub: d.obj, to: 'open' as const, label: 'Mở đối tượng' }] : []), { k: 'Việc đã xong', sub: 'Về tab Đã xong', to: 'done' }];
  v.acts = [{ k: `Đánh dấu xong ${d.code}`, who: 'DAVID', when: d.at, kind: 'Xong' }];
  v.primary = open ? { label: 'Mở đối tượng', kind: 'open', target: 'Mở đối tượng' } : { label: '', kind: 'none' };
  return v;
}

export function buildDetail(kind: DetailKind, id: number | string, st: { done?: boolean; ok?: boolean } = {}): DetailView {
  if (kind === 'task') return taskView(TASKS.find((x) => x.id === id) ?? TASKS[0]!, !!st.done);
  if (kind === 'ap') return apView(APPROVALS.find((x) => x.id === id) ?? APPROVALS[0]!, !!st.ok);
  if (kind === 'del') return delView(String(id));
  return doneView(DONE.find((x) => x.id === id) ?? DONE[0]!);
}
